import { forwardRef, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Box,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { SubFormProps } from "../SubForm";
import { useFormHandlerContext } from "../../../../../controllers/formController";
import { NamePrefixProvider, useInputFullName } from "../NamePrefixContext";
import StatusMetadataChip from "../../../attributeMetadata/StatusMetadataChip";
import UserAttributeMetadataTabs from "../../../attributeMetadata/UserAttributeMetadataTabs";

interface AccordionSubFormProps extends SubFormProps {
  label: string;
  leading?: React.ReactNode;
}

const AccordionSubForm = forwardRef<HTMLDivElement, AccordionSubFormProps>(
  ({ children, name, label, leading }, ref) => {
    const {
      hasMetadataField,
      isMetadataFieldVisible,
      setMetadataFieldVisibility,
    } = useFormHandlerContext();
    const fullName = useInputFullName(name);
    const hasMetadata = hasMetadataField(fullName);
    const isMetadataVisible = isMetadataFieldVisible(fullName);
    const [metadataVisibility, setMetadataVisibility] =
      useState<boolean>(false);
    const tabState = metadataVisibility ? "metadata" : "data";
    const [expanded, setExpanded] = useState<boolean>(false);

    useEffect(() => {
      if (hasMetadata) {
        if (isMetadataVisible !== metadataVisibility) {
          setMetadataVisibility(isMetadataVisible);
          setExpanded(isMetadataVisible);
        }
      }
    }, [isMetadataVisible, hasMetadataField, metadataVisibility, hasMetadata]);

    const handleTabStateChangeInternally = (newState: "data" | "metadata") => {
      setMetadataVisibility(newState === "metadata");
      setExpanded(true);
      if (hasMetadata) {
        setMetadataFieldVisibility(fullName, newState === "metadata");
      }
    };

    return (
      <Accordion
        ref={ref}
        disableGutters
        expanded={expanded}
        onChange={(_, newExpanded) => setExpanded(newExpanded)}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box
            sx={{ display: "flex", alignItems: "center", width: "100%", mr: 1 }}
          >
            {leading && <Box sx={{ mr: 1 }}>{leading}</Box>}
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ flexGrow: 1, lineHeight: 1 }}
            >
              {label}
            </Typography>
            {hasMetadata && (
              <StatusMetadataChip name={fullName} show={!expanded} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <UserAttributeMetadataTabs
            name={name}
            state={tabState}
            onTabChange={handleTabStateChangeInternally}
          >
            <NamePrefixProvider prefix={name}>{children}</NamePrefixProvider>
          </UserAttributeMetadataTabs>
        </AccordionDetails>
      </Accordion>
    );
  }
);

AccordionSubForm.displayName = "AccordionSubForm";

export default AccordionSubForm;
